import { useCallback, useContext, useState } from "react";
import { loginService, logoutService, userPermissions, userSettings } from "services/loginService";
import Context from "contextAPI/UserContext";
import { useHistory } from "react-router-dom";
import {
  ACCESS_TOKEN_NAME,
  USER_DETAIL,
  DOMAIN,
  TYPE,
  ADD_REGISTER,
  EDIT_REGISTER,
  VIEW_REGISTER,
  UPLOAD_FILES,
  CREATE_EXPEDIENT,
  SET_PATH_EXPEDIENT,
  DELETE_PATH_EXPEDIENT,
  VIEW_FILES,
  EDIT_FILE_DATA,
  MOVE_FILE,
  MOVE_FILE_TO_TRASH,
  SIGN_ELECTRONICALLY,
  NOTIFICATIONS,
  INTEGRATIONS,
  ROLES_PERMISSIONS,
  CANCEL_RECORD,
  ADD_WORKFLOW,
  VIEW_WORKFLOW,
  DESIGNER_FORMS,
  DESIGNER_FORMS_PERMISSIONS,
  PASSWORD_EXPIRATION_DAYS,
  WORKFLOW_FORMS_PERMISSIONS,
  WORKFLOW_ALL_FORMS,
  FIRST_EXECUTION_BOOLEAN,
  ADDITIONAL_SERVICES,
  USER_LANGUAGE,
} from "constants/securityConst";
import { setItemLocalStorage } from "utils/localStorage";
import { enumsPermissions } from "utils/enums";
import {
  ShowAlertLoginError,
  ShowUserAlertMaxUsers,
  ShowUserAlertWithoutRoles,
} from "utils/alerts";
import { isNullOrUndefined } from "utils/validations";
import { useTranslation } from "react-i18next";
import { decryptResponse } from "utils/encryptedDecryptedData";
import swal from "sweetalert";

export default function useLogin() {
  const { t } = useTranslation();
  let history = useHistory();
  const { jwt, setJwt, setUser, state, setState, sessionReady, setSessionReady } =
    useContext(Context);
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Asynchronous function that performs user login with provided credentials and manages related state and local storage.
   * @function
   * @name login
   * @param {string} userName - The username entered by the user.
   * @param {string} password - The password entered by the user.
   * @param {function} setIsLoading - State setter function to update loading state.
   * @param {function} setJwt - State setter function to update the JWT token.
   * @param {function} setUser - State setter function to update user details.
   * @param {object} setState - State setter function to update component state.
   * @param {object} i18n - Object providing internationalization functionality.
   * @returns {void}
   */
  const login = useCallback(
    async (userName, password) => {
      try {
        setIsLoading(true);
        const response = await loginService(userName, password);
        let errorsInLogin = false;
        const loginDecrypted = decryptResponse(response);
        if (
          isNullOrUndefined(loginDecrypted.decodedToken.access) === true &&
          isNullOrUndefined(loginDecrypted.fernetKey) === true
        ) {
          errorsInLogin = true;
          setState({ loading: false, error: errorsInLogin, countError: 0 });
          return;
        }

        const access = loginDecrypted.decodedToken.access;
        const user = loginDecrypted.decodedToken.user;

        window.localStorage.setItem("KEY_35", loginDecrypted.fernetKey);

        const userDecrypted = {
          [ACCESS_TOKEN_NAME]: access,
          [DOMAIN]: user.domain,
          [TYPE]: user.user_type,
          [USER_DETAIL]: user,
          [PASSWORD_EXPIRATION_DAYS]: user.password_expiration_days,
        };

        Object.entries(userDecrypted).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            setItemLocalStorage(key, value);
          }
        });

        if (user.password_expiration_days === 0) {
          setJwt(access);
          setSessionReady(false);
          history.replace("/renew-password");
          setIsLoading(false);
          return;
        }

        const responsePermissions = await userPermissions(access);
        const permissionsDecrypted = decryptResponse(responsePermissions);

        const {
          admin_forms: { all_forms, forms_uuid },
          admin_workflows: { all_workflows, workflows_uuid },
          form_permissions,
          role_permissions,
        } = permissionsDecrypted.decodedToken.data;

        const userPermissionsDecrypted = {
          [DESIGNER_FORMS]: all_forms,
          [WORKFLOW_ALL_FORMS]: all_workflows,
          [ROLES_PERMISSIONS]: role_permissions,
          [DESIGNER_FORMS_PERMISSIONS]: forms_uuid,
          [WORKFLOW_FORMS_PERMISSIONS]: workflows_uuid,
          [ADD_REGISTER]: form_permissions[enumsPermissions.ADD_RECORD],
          [VIEW_REGISTER]: form_permissions[enumsPermissions.VIEW_RECORD],
          [EDIT_REGISTER]: form_permissions[enumsPermissions.EDIT_RECORDS],
          [CANCEL_RECORD]: form_permissions[enumsPermissions.CANCEL_RECORD],
          [NOTIFICATIONS]: form_permissions[enumsPermissions.ADD_NOTIFICATION],
          [VIEW_FILES]: form_permissions[enumsPermissions.VIEW_FILE],
          [UPLOAD_FILES]: form_permissions[enumsPermissions.UPLOAD_FILE],
          [VIEW_WORKFLOW]: form_permissions[enumsPermissions.VIEW_PROCESS],
          [ADD_WORKFLOW]: form_permissions[enumsPermissions.ADD_PROCESS],
          [EDIT_FILE_DATA]: form_permissions[enumsPermissions.EDIT_FILE_DATA],
          [CREATE_EXPEDIENT]: form_permissions[enumsPermissions.CREATE_EXPEDIENT],
          [MOVE_FILE_TO_TRASH]: form_permissions[enumsPermissions.MOVE_FILE_TO_TRASH],
          [MOVE_FILE]: form_permissions[enumsPermissions.MOVE_FILE],
          [SIGN_ELECTRONICALLY]: form_permissions[enumsPermissions.SIGN_ELECTRONICALLY],
          [SET_PATH_EXPEDIENT]: form_permissions[enumsPermissions.SET_EXPEDIENT],
          [DELETE_PATH_EXPEDIENT]: form_permissions[enumsPermissions.REMOVE_EXPEDIENT],
          [FIRST_EXECUTION_BOOLEAN]: true,
          [USER_LANGUAGE]: user.language,
        };

        Object.entries(userPermissionsDecrypted).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            setItemLocalStorage(key, value);
          }
        });

        const responseSettings = await userSettings(access);

        const userSettingsDecrypted = {
          [INTEGRATIONS]: responseSettings.data.integrations,
          [ADDITIONAL_SERVICES]: responseSettings.data.additional_services,
        };

        Object.entries(userSettingsDecrypted).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            setItemLocalStorage(key, value);
          }
        });

        setJwt(access);
        setUser(user);
        setSessionReady(true);
      } catch (errorsInLogin) {
        window.localStorage.clear();
        setState({
          loading: false,
          error: true,
          countError: state.countError,
        });

        if (
          isNullOrUndefined(errorsInLogin.response) === false &&
          errorsInLogin.response.data.code === "10024"
        ) {
          ShowUserAlertWithoutRoles();
        } else if (
          isNullOrUndefined(errorsInLogin.response) === false &&
          errorsInLogin.response.data.code === "500080"
        ) {
          ShowUserAlertMaxUsers();
        } else {
          ShowAlertLoginError();
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setJwt, setIsLoading, setState],
  );

  /**
   * Removes various items from local storage related to user session and preferences,
   * clears JWT token and user data, and redirects to the home page ("/").
   * @param {function} setJwt - State setter function to clear the JWT token.
   * @returns {void}
   */
  const logout = useCallback(() => {
    logoutService(jwt)
      .then((response) => {
        if (response.status === 200) {
          window.localStorage.clear();
          setJwt(null);
          setUser({});
          setSessionReady(false);
          history.push("/");
        } else {
          ShowAlertLoginError();
        }
      })
      .catch((error) => {
        if (error.response.data.code === "500079") {
          swal({
            title: t("modal.DoneError.header"),
            text: t("showNotification401"),
            icon: "error",
            button: t("modal.Done.footerButton"),
            dangerMode: true,
          }).then(function () {
            window.localStorage.clear();
            setJwt(null);
            setUser({});
            setSessionReady(false);
            history.push("/");
          });
        }
      });
  }, [setJwt]);

  return {
    jwt,
    isLogged: Boolean(jwt) && sessionReady === true,
    isPasswordExpired: Boolean(jwt) && !sessionReady,
    isLoginLoading: state.loading,
    hasLoginError: Boolean(state.error),
    hasLoginCount: state.countError,
    isLoading,
    login,
    state,
    logout,
  };
}
